/**
 * @file: useTypewriter.ts
 * @copyright: (c) 2019-2020 sichuan zhichetech co., ltd.
 */

import { useLocalObservable } from '@euler/utils/hooks';
import { useMemo } from 'react';
import { AccessibilityInfo } from 'react-native';
import {
  catchError,
  concat,
  from,
  map,
  of,
  startWith,
  switchMap,
  take,
  timer,
} from 'rxjs';

/** Milliseconds between two typed characters. */
const kInterval = 50;
/** The pause before the next text starts. */
const kPause = 250;

/**
 * How many characters of each text are typed, for `TypewriterText`: one
 * text after the other, never two at once. A changed text starts them all
 * over; with reduced motion on, all show at once.
 */
export function useTypewriter(texts: readonly string[]): number[] {
  const key = texts.join('\u0000');
  // Characters, not UTF-16 units, so an emoji is never cut in half.
  const lengths = useMemo(
    () => key.split('\u0000').map(text => Array.from(text).length),
    [key],
  );
  return useLocalObservable(
    () => {
      const none = lengths.map(() => 0);
      return from(AccessibilityInfo.isReduceMotionEnabled()).pipe(
        catchError(() => of(false)),
        switchMap(reduced =>
          reduced
            ? of(lengths)
            : concat(
                ...lengths.map((length, line) =>
                  timer(line === 0 ? 0 : kPause, kInterval).pipe(
                    take(length),
                    map(index =>
                      lengths.map((full, other) =>
                        other < line ? full : other === line ? index + 1 : 0,
                      ),
                    ),
                  ),
                ),
              ),
        ),
        startWith(none),
      );
    },
    () => lengths.map(() => 0),
    [lengths],
  );
}
